import { Button } from "@mui/material";
import ErrorMessage from "components/ErrorMessage";
import ODateTimePicker from "components/shared/datePicker/ODateTimePicker";
import AuthContext from "context/AuthContext";
import { isEmpty } from "lodash";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { TiTrash } from "react-icons/ti";
import { useNavigate } from "react-router-dom";
import { apiPost } from "utils/apiFetch";
import apiPath from "utils/apiPath";
import helpers from "utils/helpers";
import Input from "../../../components/Input";
import QuestionDialog from "../../../components/QuestionDialog";
import useToastContext from "hooks/useToastContext";
import { QUIZ_TEST_CATEGORIES } from "utils/constants";

function AddTestPage() {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const notification = useToastContext();
  const [title, setTitle] = useState("");
  const [titleAr, setTitleAr] = useState("");
  const [questions, setQuestions] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionAr, setDescriptionAr] = useState("");
  const [attempts, setAttempts] = useState("1");
  const [status, setStatus] = useState("active");
  const [testType, setTestType] = useState("quiz");
  const [duration, setDuration] = useState("");
  const [open, setOpen] = useState(false);
  const [passingPercentage, setPassingPercentage] = useState("");
  const [canSaveAndFinishLater, setCanSaveAndFinishLater] = useState(false);
  const [canGoBackToPreviousQuestion, setCanGoBackToPreviousQuestion] =
    useState(false);
  const [showTimer, setShowTimer] = useState(false);
  const [category, setCategory] = useState(null);
  const navigate = useNavigate();
  function questionAddedHandler(question) {
    setQuestions([...questions, question]);
    setOpen(false);
  }

  function deleteQuestionHandler(index) {
    setQuestions(questions.filter((q, i) => i !== index));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!isValid()) return;

    try {
      const payload = {
        title,
        titleAr,
        description,
        descriptionAr,
        startTime: new Date(startDate),
        endTime: new Date(endDate),
        attempts,
        status,
        testType,
        passingPercentage,
        canGoBackToPreviousQuestion,
        canSaveAndFinishLater,
        showTimer,
        duration,
        category: testType === "practice" ? category : null,
      };

      const response = await apiPost(apiPath.quizTests, payload);
      if (!response?.data?.success) return;

      const testId = response?.data?.results?._id;

      for (const question of questions) {
        const { data } = await apiPost(apiPath.quizQuestions, {
          testId,
          title: question.title,
          titleAr: question.titleAr,
          description: question.description,
          descriptionAr: question.descriptionAr,
          type: question.type,
          points: question.points,
          expectedAnswer: question.expectedAnswer,
          options: question.options,
          isTrue: question.isTrue,
          matchingPairs: question.matchingPairs,
        });
        if (!data?.success) return;
      }
      notification.success("Test Added Successfully");
      navigate("/test-manager/tests");
    } catch (error) {
      console.log(error);
      notification.error(error?.response?.data?.message);
    }
  }

  function isValid() {
    const isStartBeforeEnd =
      startDate && endDate && new Date(startDate) < new Date(endDate);

    const isCategoryValid = testType !== "practice" || !isEmpty(category);

    return !(
      isEmpty(title) ||
      isEmpty(titleAr) ||
      isEmpty(description) ||
      isEmpty(descriptionAr) ||
      !startDate ||
      !endDate ||
      !isStartBeforeEnd ||
      isEmpty(attempts) ||
      attempts < 1 ||
      isEmpty(questions) ||
      duration < 1 ||
      isEmpty(passingPercentage) ||
      passingPercentage < 1 ||
      passingPercentage > 100 ||
      !isCategoryValid
    );
  }

  return (
    <div className="bg-white px-3 py-4 ">
      <h1 className="text-2xl font-bold">{t("O_ADD_TEST")}</h1>
      <form className="flex flex-col gap-4 " onSubmit={handleSubmit}>
        <div className="flex gap-4">
          <Input
            label={t("O_TITLE")}
            placeholder={t("O_TITLE")}
            required
            name="title"
            onChange={(e) => setTitle(e.target.value)}
          />
          <Input
            label={t("O_TITLE") + t("O_ARABIC")}
            placeholder={t("O_TITLE") + t("O_ARABIC")}
            required
            name="titleAr"
            onChange={(e) => setTitleAr(e.target.value)}
          />
        </div>
        <div className="flex gap-4">
          <Input
            label={t("O_DESCRIPTION")}
            placeholder={t("O_DESCRIPTION")}
            required
            name="description"
            onChange={(e) => setDescription(e.target.value)}
          />
          <Input
            label={t("O_DESCRIPTION") + t("O_ARABIC")}
            placeholder={t("O_DESCRIPTION") + t("O_ARABIC")}
            required
            name="descriptionAr"
            onChange={(e) => setDescriptionAr(e.target.value)}
          />
        </div>

        <div className="flex gap-4">
          <div className="flex-1">
            <ODateTimePicker
              name="startDate"
              placeholder={t("O_START_TIME")}
              value={startDate}
              handleDateChange={setStartDate}
              inputLabel={t("O_START_TIME")}
              startDate={startDate}
              maxDate={endDate}
            />
          </div>
          <div className="flex-1">
            <ODateTimePicker
              name="endDate"
              placeholder={t("O_END_TIME")}
              value={endDate}
              handleDateChange={setEndDate}
              inputLabel={t("O_END_TIME")}
              startDate={endDate}
              minDate={startDate || "today"}
            />
            {startDate &&
              endDate &&
              new Date(endDate) <= new Date(startDate) && (
                <ErrorMessage
                  message={
                    t("O_END_TIME_MUST_BE_AFTER_START_TIME") ||
                    "End time must be after start time"
                  }
                />
              )}
          </div>
        </div>

        <div className="flex gap-4">
          <div className="flex-1">
            <label htmlFor="testType">
              {"Can go back to previous question?"}
            </label>
            <select
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              name="canGoBackToPreviousQuestion"
              id="canGoBackToPreviousQuestion"
              onChange={(e) =>
                setCanGoBackToPreviousQuestion(e.target.value === "true")
              }
            >
              <option value="false" defaultChecked>
                No
              </option>
              <option value="true">Yes</option>
            </select>
          </div>
          <div className="flex-1">
            <label htmlFor="testType">{"Can Save and finish later?"}</label>
            <select
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              name="canSaveAndFinishLater"
              id="canSaveAndFinishLater"
              onChange={(e) =>
                setCanSaveAndFinishLater(e.target.value === "true")
              }
            >
              <option value="false" defaultChecked>
                No
              </option>
              <option value="true">Yes</option>
            </select>
          </div>
        </div>

        <div className="flex gap-4">
          <Input
            type="text"
            label={t("O_NUMBER_OF_ATTEMPTS")}
            required
            name="attempts"
            min={1}
            max={10}
            value={attempts}
            onChange={(e) => {
              const value = e.target.value;
              if (!value.match(/^[0-9]*$/)) {
                return;
              }

              setAttempts(value);
            }}
            disabled={testType === "quiz"}
          />
          <Input
            type="text"
            label={t("O_DURATION_MINUTES")}
            required
            name="duration"
            value={duration}
            onChange={(e) => {
              const value = e.target.value;
              if (!value.match(/^[0-9]*$/)) {
                return;
              }
              if (+value < 1) {
                setDuration("");
                return;
              }
              setDuration(value);
            }}
          />
          <div className="py-3 mb-1 flex-1">
            <span className="block mb-2 text-sm font-medium text-gray-900">
              Show timer
            </span>
            <label
              htmlFor="showTimer"
              className="flex items-center gap-3 w-full p-2.5 bg-gray-50 border border-gray-300 rounded-lg cursor-pointer select-none transition-colors hover:bg-gray-100 focus-within:border-black focus-within:ring-0"
            >
              <input
                type="checkbox"
                name="showTimer"
                id="showTimer"
                checked={showTimer}
                onChange={(e) => setShowTimer(e.target.checked)}
                className="h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 accent-blue-600 focus:outline-none focus:ring-0 focus:ring-offset-0"
              />
              <span className="text-sm font-medium text-gray-700">
                {showTimer ? "Enabled" : "Disabled"}
              </span>
            </label>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="testType">{t("O_TEST_TYPE")}</label>
          <select
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            name="testType"
            id="testType"
            onChange={(e) => {
              const value = e.target.value;

              setAttempts("1");
              setTestType(value);

              if (value !== "practice") {
                setCategory(null);
              }
            }}
            value={testType}
          >
            <option value="quiz">{t("O_QUIZ")}</option>
            <option value="practice">{t("O_PRACTICE")}</option>
          </select>
        </div>
        {testType === "practice" && (
          <div className="flex flex-col gap-2">
            <label htmlFor="category">{t("O_CATEGORY")}</label>
            <select
              required={testType === "practice"}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              name="category"
              id="category"
              onChange={(e) =>
                setCategory(e.target.value === "" ? null : e.target.value)
              }
              value={category ?? ""}
            >
              <option value="">{t("O_SELECT_CATEGORY")}</option>

              {QUIZ_TEST_CATEGORIES.map((item) => (
                <option key={item._id} value={item._id}>
                  {item.title}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="flex flex-col gap-2">
          <label htmlFor="status">{t("O_STATUS")}</label>
          <select
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            name="status"
            id="status"
            onChange={(e) => setStatus(e.target.value)}
            value={status}
          >
            <option value="active">{t("O_ACTIVE")}</option>
            <option value="inactive">{t("O_INACTIVE")}</option>
          </select>
        </div>
        <div className="flex flex-col gap-2">
          <Input
            label={"Passing Percentage"}
            placeholder={"Passing Percentage"}
            required
            name="passingPercentage"
            value={passingPercentage}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "") {
                setPassingPercentage("");
                return;
              }
              if (!value.match(/^[0-9]*$/)) {
                return;
              }
              if (+value < 0) {
                setPassingPercentage("0");
              } else if (+value > 100) {
                setPassingPercentage("100");
              } else {
                setPassingPercentage(value);
              }
            }}
          />
        </div>

        <hr className="my-4" />

        <div className="flex flex-col gap-4">
          <QuestionsTable
            questions={questions}
            deleteQuestionHandler={deleteQuestionHandler}
          />
        </div>
        <Button
          type="button"
          variant="contained"
          className="self-center"
          sx={{
            backgroundColor: "#8FC150",
            "&:hover": { backgroundColor: "#7eae43" },
          }}
          onClick={() => setOpen(true)}
        >
          {t("O_ADD_QUESTION")}
        </Button>
        <QuestionDialog
          questionAddedHandler={questionAddedHandler}
          closeHandler={() => setOpen(false)}
          open={open}
        />
        <hr className="my-4" />
        <button
          type="submit"
          disabled={!isValid()}
          className={`bg-[#8FC150] hover:bg-[#7eae43] transition-colors duration-150 text-white px-4 py-2 rounded-md text-nowrap self-center disabled:bg-gray-500 disabled:hover:bg-gray-500 ${
            !isValid() ? "bg-gray-500" : ""
          }`}
        >
          {t("O_ADD_TEST")}
        </button>
      </form>
    </div>
  );
}

export default AddTestPage;

function QuestionsTable({ questions, deleteQuestionHandler }) {
  const { t } = useTranslation();
  if (questions?.length === 0 || isEmpty(questions)) return null;
  return (
    <table className="w-full text-sm text-left text-black  ">
      <thead className="text-sm   border border-[#E1E6EE]  bg-gray-700  text-white">
        <tr className="py-3 px-6">
          <th className="py-3 px-6">{t("O_TITLE")}</th>
          <th className="py-3 px-6">{t("O_TITLE") + t("O_ARABIC")}</th>
          <th className="py-3 px-6">{t("O_DESCRIPTION")}</th>
          <th className="py-3 px-6">{t("O_DESCRIPTION") + t("O_ARABIC")}</th>
          <th className="py-3 px-6">{t("O_TYPE")}</th>
          <th className="py-3 px-6">{t("O_POINTS")}</th>
          <th className="py-3 px-6">{t("O_EXPECTED_ANSWER")}</th>
          <th className="py-3 px-6">{t("O_ACTION")}</th>
        </tr>
      </thead>
      <tbody>
        {questions.map((question, index) => (
          <tr className="py-3 px-6 border-b border-[#E1E6EE]" key={index}>
            <td className="py-3 px-6">{question.title || "-"}</td>
            <td className="py-3 px-6">{question.titleAr || "-"}</td>
            <td className="py-3 px-6">{question.description || "-"}</td>
            <td className="py-3 px-6">{question.descriptionAr || "-"}</td>
            <td className="py-3 px-6">{question.type || "-"}</td>
            <td className="py-3 px-6">{question.points || "-"}</td>
            <td className="py-3 px-6">{question.expectedAnswer || "-"}</td>
            <td className="py-3 px-6 flex gap-2">
              <button
                type="button"
                className="bg-red-500 hover:bg-red-700 transition-colors duration-150 text-white px-4 py-2 rounded-md"
                onClick={() =>
                  helpers.alertFunction(
                    `Are you sure want to delete ${
                      question.title || "this question"
                    }?`,
                    index,
                    deleteQuestionHandler,
                    true,
                  )
                }
              >
                <TiTrash />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
